<template>
  <jz-container :class="['exercise-list', itemType]">
    <!-- chapter -->
    <div v-if="itemType === 'chapter'" class="title" @click="toggleCollapse" :id="this.item.uuid">
      <span class="text">{{ item.title }}</span>
      <span :class="['toggle', collapsed ? 'closed' : 'opened']">
        <jz-icon :src="icons.chevronUp" />
      </span>
    </div>

    <!-- section -->
    <div v-else-if="itemType === 'section'" class="title" :id="this.item.uuid">
      {{ item.title }}
    </div>

    <!-- exercise -->
    <exercise-list-item
      v-else-if="itemType === 'exercise'"
      :exercise="item"
      :completion-icon="trophyIcon"
      @click.native="goToExerciseFromExerciseEntry(item, null, courseUuid)"
      :id="this.item.uuid"
    />

    <jz-container
      v-if="item.contents"
      :class="['contents', collapsed ? 'closed' : 'opened']"
      :style="calculateChapterHeight(item)"
    >
      <template v-for="(listItem, index) in item.contents">
        <exercise-list
          :key="index"
          :item="listItem"
          :course-uuid="courseUuid"
          :course-history="courseHistory"
        />
      </template>
    </jz-container>
  </jz-container>
</template>

<script>
import { CONTENT_TYPE, VUEX_ROOT_ACTION_NAMES, VUEX_ROOT_GETTER_NAMES } from "@tt-app/constants";

import ExerciseList from "./ExerciseList.vue";
import ExerciseListItem from "./ExerciseListItem.vue";

import iconChevronUp from "@joyz/app-core/assets/icons/transparent/ChevronUp.svg";
import iconTrophyBronze from "@joyz/app-core/assets/trophy/small/Bronze.svg";
import iconTrophyGold from "@joyz/app-core/assets/trophy/small/Gold.svg";
import iconTrophySilver from "@joyz/app-core/assets/trophy/small/Silver.svg";
import iconChecked from "@joyz/app-core/assets/icons/Correct.svg";
import GoToExerciseMixin from "./mixins/GoToExerciseMixin";

// TODO: Update this component with types once typescript is update beyond 3.7
export default {
  name: "ExerciseList",
  components: {
    ExerciseList,
    ExerciseListItem
  },
  mixins: [GoToExerciseMixin],
  props: {
    courseHistory: {
      type: Object,
      default: () => undefined
    },
    courseUuid: {
      type: String,
      required: true
    },
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    const openedChapters = this.$store.getters[VUEX_ROOT_GETTER_NAMES.OPENED_CHAPTERS][
      this.courseUuid
    ];

    const collapsedByDefault = this.item.collapse && this.item.default === "CLOSED";

    let open = false;
    if (openedChapters) {
      open = openedChapters[this.item.uuid];
    }

    return {
      collapsed: open ? false : collapsedByDefault,
      icons: {
        chevronUp: iconChevronUp,
        trophies: {
          3: iconTrophyGold,
          2: iconTrophySilver,
          1: iconTrophyBronze
        }
      }
    };
  },
  computed: {
    itemType() {
      return CONTENT_TYPE[this.item.contentType];
    },
    trophyIcon() {
      const history = this.courseHistory && this.courseHistory.exercises[this.item.uuid];
      if (history) {
        // Trophy for exercise requiring user input
        if (history.trophy) {
          return this.icons.trophies[history.trophy];
        }
        // Demo conversation does not require user input so there is no grading, just a check
        else if (history.finished) {
          return iconChecked;
        }
      }

      return "";
    },
    calculateChapterHeight() {
      return chapter => {
        if (!chapter.contents || this.collapsed) {
          return null;
        }

        let heightOfContent = 0;

        const chapterContents = chapter.contents;
        chapterContents.forEach(chapter => {
          heightOfContent += 68;
          heightOfContent += this.heightOfLines(chapter);

          const sectionContents = chapter.contents;
          if (sectionContents) {
            sectionContents.forEach(contents => {
              heightOfContent += 68;
              heightOfContent += this.heightOfLines(contents);
            });
          }
        });

        return { "max-height": `${heightOfContent}px` };
      };
    }
  },
  methods: {
    heightOfLines(contents) {
      const maxCharacters = 15;

      const clampedLineCount = Math.max(1, contents.title.length / maxCharacters);

      const roundedLineCount = Math.ceil(clampedLineCount);

      return roundedLineCount * 16;
    },

    scrollToContainer(itemUuid) {
      const scrollTarget = document.getElementById(itemUuid);

      const parent = scrollTarget.closest("#course-chapters");
      const parentTop = parent.getBoundingClientRect().top + window.pageYOffset;

      scrollTarget.closest(".jz-container").scrollIntoView({
        to: parentTop,
        behavior: "smooth"
      });
    },

    async toggleCollapse() {
      this.collapsed = !this.collapsed;

      const payload = {
        courseUuid: this.courseUuid,
        chapterUuid: this.item.uuid
      };

      if (!this.collapsed) {
        // Allow time for the animation to finish before attempting to scroll
        await new Promise(resolve => {
          setTimeout(resolve, 200);
        });

        this.scrollToContainer(this.item.uuid);

        payload.open = true;
      } else {
        payload.open = false;
      }

      this.$store.dispatch(`${VUEX_ROOT_ACTION_NAMES.SAVE_OPENED_CHAPTER}`, payload);
    }
  }
};
</script>

<style lang="scss">
.exercise-list.chapter {
  & > .title {
    align-items: center;
    background-color: var(--color-primary-alternate-background-light);
    border-bottom: 1px solid rgba(var(--rgb-black), 0.08);
    display: flex;
    font-size: $font-size-sm;
    font-weight: bold;
    justify-content: space-between;
    padding: $gutter;

    &:hover {
      cursor: pointer;
    }

    .toggle {
      height: 24px;
      transition: all 0.5s;

      &.closed {
        transform: rotate(180deg);
      }
    }
  }

  & > .contents {
    overflow: hidden;
    transition: all 0.5s;

    &.closed {
      max-height: 0;
    }
  }
}

.exercise-list.section {
  & > .title {
    padding: ($gutter / 2) ($gutter * 1.5);
    background-color: var(--color-light-background-light);
    font-weight: 500;
    font-size: $font-size-xs;
  }
}

.exercise-list.exercise {
  border-bottom: 1px solid rgba(var(--rgb-black), 0.08);
}
</style>
